import {
  Component,
  Host,
  h,
  Prop,
  Listen,
  Element,
  Event,
  EventEmitter,
  Watch,
  State,
} from '@stencil/core';
import { AhaTreeItem } from '../aha-tree-item/aha-tree-item';

export interface AhaTree extends HTMLAhaTreeElement {}

@Component({
  tag: 'aha-tree',
  styleUrl: 'aha-tree.css',
  shadow: true,
})
export class AhaTree {
  @Element() host!: HTMLAhaTreeElement;

  @State() slotLoadingComplete: boolean = false;

  @Prop({ reflect: true }) lines?: boolean;

  setItemHiddenByExpansionLevel(item: HTMLAhaTreeItemElement, items: HTMLAhaTreeItemElement[]) {
    if (!(item instanceof AhaTreeItem)) return;
    if (item.level > this.expansionLevel + 1) {
      let hiddenBy = this.hiddenByMap.get(item);
      if (!hiddenBy) {
        const parent = this.itemParent(item);
        if (parent) {
          hiddenBy = parent;
          this.hiddenByMap.set(item, parent);
        }
      }
      item.hidden = true;
    }
    // Only set the `aria-expanded` attribute if this is actually a group. Leaf nodes should not have aria-expanded set at all.
    if (item.hasChildren(items)) item.expanded = item.level <= this.expansionLevel;
  }

  get normalizedExpansionLevel() {
    const level = Math.max(this.expansionLevel, 0);
    return Number.isNaN(level) ? Infinity : level;
  }

  @Prop({ reflect: true }) expansionLevel: number = Infinity;
  @Watch('expansionLevel')
  expansionLevelChanged() {
    if (this.expansionLevel !== this.normalizedExpansionLevel) {
      this.expansionLevel = this.normalizedExpansionLevel;
    } else {
      // Set the expanded state of each item group based on the normalized expansion level.
      const items = this.items;
      items.forEach(item => {
        this.setItemHiddenByExpansionLevel(item, items);
      });
    }
  }

  private hiddenByMap = new WeakMap<HTMLAhaTreeItemElement, HTMLAhaTreeItemElement>();

  get items() {
    return Array.from(this.host.querySelectorAll<HTMLAhaTreeItemElement>(':scope aha-tree-item'));
  }

  get selectedItem() {
    return this.host.querySelector<HTMLAhaTreeItemElement>(
      ':scope aha-tree-item[aria-selected="true"]',
    );
  }

  selectItemAtIndex(index: number) {
    const item = this.items[index];
    if (item instanceof AhaTreeItem) item.selected = true;
  }

  get focusedItem() {
    const activeElem = document.activeElement;
    if (activeElem instanceof AhaTreeItem && this.items.includes(activeElem)) {
      return activeElem;
    } else {
      return null;
    }
  }

  focusItemAtIndex(index: number) {
    const item = this.items[index];
    if (item instanceof AhaTreeItem) item.focus();
  }

  itemParent(item: HTMLAhaTreeItemElement, items = this.items) {
    const index = items.indexOf(item);
    const level = Number(item.ariaLevel);
    let siblingIndex = index - 1;

    while (siblingIndex > -1) {
      const sibling = items[siblingIndex];
      if (sibling && Number(sibling.ariaLevel) === level - 1) {
        return sibling;
      }
      siblingIndex = siblingIndex - 1;
    }
    return null;
  }

  itemAncestors(
    item: HTMLAhaTreeItemElement,
    ancestors: HTMLAhaTreeItemElement[] = [],
    items = this.items,
  ): HTMLAhaTreeItemElement[] {
    const index = items.indexOf(item);
    const level = Number(item.ariaLevel);
    let siblingIndex = index - 1;

    while (siblingIndex > -1) {
      const sibling = items[siblingIndex];
      if (sibling) {
        if (Number(sibling.ariaLevel) === level - 1) {
          ancestors.push(sibling);
          return this.itemAncestors(sibling, ancestors, items);
        }
        siblingIndex = siblingIndex - 1;
      } else {
        break;
      }
    }
    return ancestors;
  }

  itemDescendants(item: HTMLAhaTreeItemElement): HTMLAhaTreeItemElement[] {
    const descendants = [];
    const items = this.items;
    const index = items.indexOf(item);
    const level = Number(item.ariaLevel);
    let siblingIndex = index + 1;

    while (siblingIndex < items.length) {
      const sibling = items[siblingIndex];
      if (sibling) {
        if (Number(sibling.ariaLevel) > level) {
          descendants.push(sibling);
          siblingIndex = siblingIndex + 1;
        } else {
          break;
        }
      } else {
        break;
      }
    }
    return descendants;
  }

  // Navigate to either the next or previous `aha-tree-item`.
  private navigatePageTreeVertically(
    direction: 'next' | 'previous',
    item: HTMLAhaTreeItemElement | null = null,
  ) {
    item ||= this.focusedItem || this.selectedItem;
    if (!item) return;
    const items = this.items;
    const index = items.indexOf(item);
    const nextItemIndex = direction === 'next' ? index + 1 : index - 1;

    let nextItem = items[nextItemIndex];
    if (nextItem) {
      if (nextItem.ariaHidden === 'true') {
        this.navigatePageTreeVertically(direction, nextItem);
      } else {
        this.scrollToPageItem(nextItem);
        nextItem.focus();
      }
    }
  }

  // Scroll the view to the specified `aha-tree-item`.
  private scrollToPageItem(elem: HTMLAhaTreeItemElement) {
    const elemCoords = elem.getBoundingClientRect();
    const treeCoords = elem.closest('aha-tree')?.getBoundingClientRect();

    if (treeCoords && elemCoords) {
      if (elemCoords.top < treeCoords.top || elemCoords.bottom > treeCoords.bottom) {
        elem.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }

  private processItemAttributes(elem: HTMLAhaTreeItemElement, attr: string) {
    if (!elem || !attr || !(elem instanceof AhaTreeItem)) return;
    const value = elem.getAttribute(attr);
    const index = this.items.indexOf(elem);

    switch (attr) {
      case 'aria-selected':
        if (value === 'true') {
          // If an element is selected, then:
          //   * un-select any other selected item, and make it un-tabbable
          //   * make this item visible, and make it tabable
          //   * if the selected item is a group, then expand it
          //   * ensure that the mobile nav is closed.
          elem
            .closest('*[role="tree"]')
            ?.querySelectorAll<HTMLAhaTreeItemElement>(':scope aha-tree-item[aria-selected="true"]')
            .forEach(item => {
              if (item === elem) return;
              item.ariaSelected = 'false';
            });
          elem.ariaHidden = 'false';
          this.itemAncestors(elem).forEach(ancestor => {
            if (ancestor instanceof AhaTreeItem) ancestor.expanded = true;
          });
          if (elem.hasChildren(this.items)) {
            elem.expanded = true;
          }
          this.selectEvent.emit({ item: elem, index: this.items.indexOf(elem) });
        }
        break;

      case 'aria-expanded':
        if (value === 'true') {
          elem.ariaHidden = 'false';
          const descendants = this.itemDescendants(elem);
          descendants.forEach(descendant => {
            const hiddenBy = this.hiddenByMap.get(descendant);
            if (hiddenBy && hiddenBy !== elem) {
              return;
            }
            this.hiddenByMap.delete(descendant);
            descendant.ariaHidden = 'false';
          });
          this.expandedEvent.emit({ item: elem, index });
        } else if (value === 'false') {
          const descendants = this.itemDescendants(elem);
          descendants.forEach(descendant => {
            const hiddenBy = this.hiddenByMap.get(descendant);
            if (!hiddenBy) {
              this.hiddenByMap.set(descendant, elem);
              descendant.ariaHidden = 'true';
            }
            this.collapsedEvent.emit({ item: elem, index });
          });
        }
        break;

      case 'aria-hidden':
        if (value === 'false') {
          const ancestors = this.itemAncestors(elem);
          ancestors.forEach(ancestor => {
            ancestor.ariaExpanded = 'true';
          });
        } else if (value === 'true') {
          const descendants = this.itemDescendants(elem);
          descendants.forEach(descendant => {
            if (!this.hiddenByMap.has(descendant)) {
              this.hiddenByMap.set(descendant, elem);
              descendant.ariaHidden = 'true';
            }
          });
        }
        break;

      default:
        break;
    }
  }

  collapseAll() {
    this.expansionLevel = 0;
  }

  expandAll() {
    this.expansionLevel = Infinity;
  }

  // Sync the selected item with the current window.location. This is useful for ensuring that turbolinks navigation does not lose the selected item.
  syncSelectedWithLocation() {
    const { href, pathname } = window.location;
    const cleanHref = href.split('?')[0]; // Remove any query parameters
    const anchor = this.host.querySelector<HTMLAnchorElement>(
      `:scope aha-tree-item :is(a[href="${CSS.escape(cleanHref)}"], a[href="${CSS.escape(
        pathname,
      )}"])`,
    );
    if (!anchor) return;
    const selectedItem = anchor.closest<HTMLAhaTreeItemElement>('aha-tree-item');
    if (!(selectedItem instanceof AhaTreeItem)) return;
    selectedItem.selected = true;
  }

  private processAddedNodes(nodes: NodeList, items = this.items) {
    nodes.forEach(item => {
      if (!(item instanceof AhaTreeItem)) return;
      if (item.closest('aha-tree') !== this.host) return;
      const parentItem = this.itemParent(item);
      if (parentItem instanceof AhaTreeItem) {
        if (item.hasChildren(items)) item.expanded = item.level <= this.expansionLevel;

        // If the parent is expanded, then find the item that hid the parent, if any.
        // If the parent is collapsed, then the item that hid the item is the parent.
        const hiddenBy = parentItem.expanded ? this.hiddenByMap.get(parentItem) : parentItem;

        if (hiddenBy) {
          this.hiddenByMap.set(item, hiddenBy);
          item.ariaHidden = 'true';
        } else {
          // If the parent is not hidden, then show the item if the item's level is greater than the expansion level.
          item.ariaHidden = (item.level > this.expansionLevel).toString();
        }
      } else {
        item.ariaHidden = (item.level > this.expansionLevel).toString();
      }
    });
  }

  private itemsChangedObs = new MutationObserver(mutations => {
    const items = this.items;
    mutations.forEach(mutation => {
      const target = mutation.target;
      if (!(target instanceof AhaTreeItem)) return;
      if (
        mutation.type === 'attributes' &&
        mutation.attributeName &&
        target.role === 'treeitem' &&
        target.closest('aha-tree') === this.host
      ) {
        this.processItemAttributes(target, mutation.attributeName);
      } else if (mutation.type === 'childList') {
        if (mutation.addedNodes) {
          this.processAddedNodes(mutation.addedNodes, items);
        }
      }
    });
  });

  connectedCallback() {
    this.syncSelectedWithLocation();
  }

  componentDidLoad() {
    // Normalize the expansion level.
    this.expansionLevelChanged();
  }

  // The number of children in the slot
  numSlotChangeSlottedChildren = 0;

  // A requestAnimationFrame to track when the slot is fully loaded.
  slotChangeRAF: number | null = null;

  // A requestAnimationFrame to track when the slot is fully loaded and the tree contents are visible.
  loadingCompleteRAF: number | null = null;

  // A requestAnimationFrame debounced method that tracks when all the contents of an aha-tree can be considered "loaded" within the slot. Used a RaF debounce pattern rather than the typical `debounce` function from lodash, as that function relies on timing (ex. how many milliseconds have passed since the last call) rather than the actual state of the DOM.
  waitForSlottedChildren = () => {
    // Any time the slot has new elements added to/removed from it we should treat the state as "loading".
    this.slotLoadingComplete = false;

    if (this.slotChangeRAF) cancelAnimationFrame(this.slotChangeRAF);

    this.slotChangeRAF = requestAnimationFrame(() => {
      const numItems = this.items.length;

      if (this.numSlotChangeSlottedChildren === numItems) {
        this.numSlotChangeSlottedChildren = 0;
        const items = this.items;
        const numItems = items.length;

        items.forEach((item, index) => {
          // Set the aria attributes for position in the set and the size of the set.
          item.setAttribute('aria-posinset', (index + 1).toString());
          item.setAttribute('aria-setsize', numItems.toString());

          // Set the exmpaned state of each item group based on the normalized expansion level.
          this.setItemHiddenByExpansionLevel(item, items);
        });

        // Wait a tick so that any other attribute changes can be reflected to the DOM
        // before the observer is set up and the initial selected item is processed.
        if (this.loadingCompleteRAF) cancelAnimationFrame(this.loadingCompleteRAF);
        this.loadingCompleteRAF = requestAnimationFrame(() => {
          if (!this.slotLoadingComplete) {
            // Set up the observer to watch for changes to the items. Only do this the first time the tree is
            // rendered, so that we don't set up multiple observers.
            this.itemsChangedObs.observe(this.host, {
              childList: true,
              subtree: true,
              attributes: true,
              attributeFilter: ['aria-expanded', 'aria-selected'],
            });
          }

          // If there is a selected item, then do all the side effects of selecting an item.
          const selectedItem = this.selectedItem;
          if (selectedItem) this.processItemAttributes(selectedItem, 'aria-selected');

          this.slotLoadingComplete = true;

          // Wait a tick before emitting the event. This ensures that the tree is fully loaded, visible, and the correct height.
          requestAnimationFrame(() => {
            this.loadedEvent.emit({ element: this.host, items: this.items });
          });
        });
      } else {
        this.numSlotChangeSlottedChildren = numItems;
        this.waitForSlottedChildren();
      }
    });
  };

  @Listen('keydown', {
    passive: false, // TSC: fix "Use vDOM listener instead" error.
  })
  handleKeyDown(e: KeyboardEvent) {
    if (e.defaultPrevented) return;

    const tree = e.currentTarget;
    if (!(tree instanceof AhaTree)) return;
    const item = tree.focusedItem || tree.selectedItem;
    if (!(item instanceof AhaTreeItem)) return;

    const items = tree.items;
    if (tree && tree.contains(item)) {
      switch (e.key) {
        case 'ArrowDown':
          // Move focus to the next `aha-tree-item`.
          e.preventDefault();
          tree.navigatePageTreeVertically('next');
          break;
        case 'ArrowUp':
          // Move focus to the previous `aha-tree-item`.
          e.preventDefault();
          tree.navigatePageTreeVertically('previous');
          break;
        case 'ArrowRight':
          e.preventDefault();
          if (item) {
            if (item.ariaExpanded === 'false') {
              // If the `aha-tree-item` is a group, and that group is closed, then expand it.
              item.ariaExpanded = 'true';
            } else if (item.ariaExpanded === 'true') {
              // If the `aha-tree-item` is a group, and that group is open, then move focus to the first child of that group (which happens to be the next element).
              const nextItem = item.nextElementSibling;
              if (nextItem instanceof AhaTree) {
                nextItem.focus();
              }
            }
          }
          break;
        case 'ArrowLeft':
          e.preventDefault();
          if (item) {
            if (item.ariaExpanded === 'true') {
              // If the `aha-tree-item` is a group, and that group is open, then collapse it.
              item.ariaExpanded = 'false';
            } else {
              // If the `aha-tree-item` is a group, and that group is closed, then move focus to the parent group (the parent "group" is whatever node came previously in the tree which has a lower aria-level attribute).
              let parent = tree.itemParent(item);
              while (parent && parent.hasAttribute('aria-level')) {
                if (Number(parent.ariaLevel) < Number(item.ariaLevel)) {
                  parent.focus();
                  break;
                }
                parent = tree.itemParent(parent);
              }
            }
          }
          break;
        case 'Home':
          e.preventDefault();
          const firstItem = items.find(item => item.ariaHidden === 'false');
          if (firstItem) {
            tree.scrollToPageItem(firstItem);
            firstItem.focus();
          }
          break;
        case 'End':
          e.preventDefault();
          const lastItem = items.reverse().find(item => item.ariaHidden === 'false');
          if (lastItem) {
            tree.scrollToPageItem(lastItem);
            lastItem.focus();
          }
          break;
        case 'Enter':
          item.ariaSelected = 'true';
          item.focus();

          // If the item is a group, then expand it.
          if (item.ariaExpanded === 'false') {
            item.ariaExpanded = 'true';
          }

          // If the item has a child link, then click it.
          const childLink = item.childLink;
          if (childLink) {
            childLink.click();
          }
          break;
        default:
          break;
      }
    }
  }

  @Event({
    eventName: 'select',
    cancelable: true,
    bubbles: true,
  }) //eslint-disable-line @stencil/decorators-style
  selectEvent!: EventEmitter;

  @Event({
    eventName: 'expanded',
    cancelable: true,
    bubbles: true,
  }) //eslint-disable-line @stencil/decorators-style
  expandedEvent!: EventEmitter;

  @Event({
    eventName: 'collapsed',
    cancelable: true,
    bubbles: true,
  }) //eslint-disable-line @stencil/decorators-style
  collapsedEvent!: EventEmitter;

  @Event({
    eventName: 'loaded',
    cancelable: true,
    bubbles: true,
  }) //eslint-disable-line @stencil/decorators-style
  loadedEvent!: EventEmitter;

  render() {
    return (
      <Host role="tree" aria-busy={this.slotLoadingComplete ? 'false' : 'true'}>
        <div id="content">
          <div id="spinner" aria-hidden={this.slotLoadingComplete ? 'true' : 'false'}>
            <aha-spinner size="2ex"></aha-spinner>
          </div>
          <slot onSlotchange={this.waitForSlottedChildren}></slot>
        </div>
      </Host>
    );
  }
}
